import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { Plus, X, Pencil, Trash2, Search, Library, Hash, Ruler, FolderKanban, Lock, Users } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";

const CONFIG = {
  "hsn-sac": {
    title: "HSN / SAC Library",
    eyebrow: "Library",
    icon: Hash,
    subtitle: "Master list of HSN (goods) and SAC (services) codes with default GST rates. When an item is mapped to a code, its GST rate auto-fills.",
    resource: "hsn-sac",
    columns: [
      { key: "code", label: "Code", className: "font-bold" },
      { key: "type", label: "Type", render: (v) => (
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${v === "HSN" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"}`}>{v}</span>
      ) },
      { key: "description", label: "Description" },
      { key: "gst_rate", label: "GST %", render: (v) => <span className="tabular font-bold text-[#F97316]">{v}%</span> },
    ],
    blank: () => ({ code: "", type: "HSN", description: "", gst_rate: 18 }),
    fields: [
      { key: "code", label: "Code *", required: true, placeholder: "e.g. 8544 / 998719" },
      { key: "type", label: "Type", type: "select", options: ["HSN", "SAC"] },
      { key: "gst_rate", label: "Default GST Rate (%)", type: "number" },
      { key: "description", label: "Description", type: "textarea", full: true },
    ],
  },
  "uom": {
    title: "UOM Library (GST-compliant)",
    eyebrow: "Library",
    icon: Ruler,
    subtitle: "Units of measurement per GSTN spec. Items pick from this master so e-invoice / GSTR-1 stays compliant.",
    resource: "uom-master",
    columns: [
      { key: "code", label: "Code", className: "font-bold" },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
      { key: "gst_compliant", label: "GST Compliant", render: (v) => v ? (
        <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5">Yes</span>
      ) : (
        <span className="text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-600 px-2 py-0.5">Custom</span>
      ) },
    ],
    blank: () => ({ code: "", name: "", description: "", gst_compliant: true }),
    fields: [
      { key: "code", label: "Code *", required: true, placeholder: "e.g. NOS / KGS / MTR" },
      { key: "name", label: "Name *", required: true, placeholder: "e.g. Numbers / Kilograms" },
      { key: "gst_compliant", label: "GST Compliant", type: "checkbox" },
      { key: "description", label: "Description", type: "textarea", full: true },
    ],
  },
  "item-groups": {
    title: "Item Groups",
    eyebrow: "Library",
    icon: Library,
    subtitle: "Logical groupings of items. Used to filter the item picker in Quotation / Purchase / Sales Invoice flows. Restrict a group to specific users to hide its items from everyone else.",
    resource: "item-groups",
    columns: [
      { key: "code", label: "Code", className: "font-bold" },
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
      { key: "access_mode", label: "User Access", render: (v, item) => v === "restricted" ? (
        <span data-testid={`group-access-chip-${item.id}`} className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 inline-flex items-center gap-1">
          <Lock size={10} /> Restricted · {(item.allowed_users || []).length} user{(item.allowed_users || []).length === 1 ? "" : "s"}
        </span>
      ) : (
        <span data-testid={`group-access-chip-${item.id}`} className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5">All Users</span>
      ) },
    ],
    blank: () => ({ code: "", name: "", description: "", access_mode: "all", allowed_users: [] }),
    fields: [
      { key: "code", label: "Code *", required: true, placeholder: "e.g. ELEC / IT / SOL" },
      { key: "name", label: "Name *", required: true },
      { key: "description", label: "Description", type: "textarea", full: true },
      { key: "access_mode", label: "User Access", type: "group-access", full: true },
    ],
  },
  "project-groups": {
    // Sprint 2026-Q1d / Asset Depreciation Step ① — admin master for the
    // Project Group classifier. The list itself is seeded by the backend on
    // first boot (Revenue / Internal Cost / Corporate / Capital) and is
    // freely extensible from this UI. The same Master is consumed by the
    // Project form, the Projects list filter, and (in upcoming phases) MA
    // roll-ups and the Depreciation Snapshot writer.
    title: "Project Groups",
    eyebrow: "Library",
    icon: FolderKanban,
    subtitle: "Classify every project as Revenue / Internal Cost / Corporate / Capital — or extend the list. Drives reporting roll-ups across MA, MIS and the upcoming Asset Depreciation snapshot.",
    resource: "project-groups",
    // Reveals the Active / Disabled dropdown in the toolbar.
    statusFilter: true,
    columns: [
      { key: "sort_order", label: "Sort",   className: "tabular text-slate-500 w-16", render: (v) => v ?? 0 },
      { key: "name",       label: "Name",   className: "font-bold" },
      { key: "description", label: "Description" },
      { key: "is_active",  label: "Status", render: (v) => v === false ? (
        <span className="text-[10px] uppercase tracking-wider font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Disabled</span>
      ) : (
        <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">Active</span>
      ) },
    ],
    blank: () => ({ name: "", description: "", sort_order: 50, is_active: true }),
    fields: [
      { key: "name", label: "Name *", required: true, placeholder: "e.g. Joint Venture / Pilot / R&D" },
      { key: "sort_order", label: "Sort Order", type: "number", placeholder: "Lower numbers appear first in dropdowns" },
      { key: "is_active", label: "Active (visible in pickers)", type: "checkbox" },
      { key: "description", label: "Description", type: "textarea", full: true },
    ],
  },
};

export default function LibraryPage({ kind: kindProp }) {
  const params = useParams();
  const kind = kindProp || params.kind;
  // Force a remount per route param so internal state resets cleanly
  return <LibraryView key={kind} kind={kind} />;
}

function LibraryView({ kind }) {
  const cfg = CONFIG[kind];
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  // Sprint 2026-Q1d — opt-in status filter (only enabled when
  // `cfg.statusFilter === true`). "" = All · "active" = is_active!==false
  // · "disabled" = is_active === false.
  const [statusFilter, setStatusFilter] = useState("");

  function load() {
    if (!cfg) return;
    api.get(`/${cfg.resource}`).then(r => setList(r.data || []));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!cfg) return <div className="p-10 text-center text-slate-500">Unknown library.</div>;

  async function del(id, label) {
    if (!window.confirm(`Delete "${label}"?`)) return;
    try {
      await api.delete(`/${cfg.resource}/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  // Sprint 2026-Q1d — quick toggle for the Active flag without opening
  // the full edit form. Only used by masters whose CONFIG declares
  // `statusFilter: true` (project-groups today).
  async function toggleActive(item) {
    try {
      await api.put(`/${cfg.resource}/${item.id}`, {
        ...item,
        is_active: !(item.is_active !== false),
      });
      toast.success(item.is_active !== false ? "Disabled" : "Enabled");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }

  const filtered = list.filter(item => {
    if (cfg.statusFilter && statusFilter) {
      const active = item.is_active !== false;
      if (statusFilter === "active"   && !active) return false;
      if (statusFilter === "disabled" &&  active) return false;
    }
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return Object.values(item).some(v => String(v).toLowerCase().includes(s));
  });

  const Icon = cfg.icon;

  return (
    <div data-testid={`lib-${kind}-page`}>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow={cfg.eyebrow}
        title={cfg.title}
        subtitle={cfg.subtitle}
        actions={
          <button data-testid="add-entry-btn" onClick={() => { setEditing(cfg.blank()); setShowForm(true); }}
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> Add Entry
          </button>
        }
      />

      <div className="relative mb-4 max-w-3xl flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
                 placeholder="Search…"
                 className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none"
                 data-testid="lib-search" />
        </div>
        {cfg.statusFilter && (
          <select
            data-testid="lib-status-filter"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#F97316]"
            title="Filter by Active / Disabled"
          >
            <option value="">All Statuses</option>
            <option value="active">Active only</option>
            <option value="disabled">Disabled only</option>
          </select>
        )}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full" data-testid={`lib-${kind}-table`}>
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              {cfg.columns.map(c => (
                <th key={c.key} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{c.label}</th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={cfg.columns.length + 1} className="p-10 text-center text-slate-500 text-sm">
                <Icon size={28} className="inline-block mb-3 text-slate-300" />
                <div>No entries. Click <strong>Add Entry</strong>.</div>
              </td></tr>
            )}
            {filtered.map(item => (
              <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                {cfg.columns.map(c => (
                  <td key={c.key} className={`py-3 px-4 text-sm text-slate-800 ${c.className || ""}`}>
                    {c.render ? c.render(item[c.key], item) : (item[c.key] ?? "—")}
                  </td>
                ))}
                <td className="py-3 px-4 text-right">
                  <div className="flex justify-end gap-2">
                    {cfg.statusFilter && (
                      <button
                        data-testid={`toggle-active-${item.id}`}
                        onClick={() => toggleActive(item)}
                        title={item.is_active !== false ? "Disable (hide from pickers)" : "Enable (show in pickers)"}
                        className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${item.is_active !== false ? "border-slate-300 text-slate-600 hover:bg-slate-50" : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"}`}
                      >
                        {item.is_active !== false ? "Disable" : "Enable"}
                      </button>
                    )}
                    <button onClick={() => { setEditing(item); setShowForm(true); }} className="text-slate-500 hover:text-[#F97316] p-1"><Pencil size={14} /></button>
                    <button onClick={() => del(item.id, item.name || item.code)} className="text-slate-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {showForm && (
        <EntryForm cfg={cfg} record={editing} onClose={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function EntryForm({ cfg, record, onClose }) {
  const [form, setForm] = useState(record);
  const [saving, setSaving] = useState(false);
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function save(e) {
    e.preventDefault();
    for (const f of cfg.fields) {
      if (f.required && !form[f.key]) {
        toast.error(`${f.label.replace(" *", "")} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      if (form.id) await api.put(`/${cfg.resource}/${form.id}`, form);
      else await api.post(`/${cfg.resource}`, form);
      toast.success(form.id ? "Updated" : "Created");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">{form.id ? "Edit" : "New"} Entry</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={save} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {cfg.fields.map(f => (
            <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
              <Field label={f.label}>
                {f.type === "group-access" ? (
                  <GroupAccessField form={form} set={set} />
                ) : f.type === "select" ? (
                  <select value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} className={inputCls} data-testid={`field-${f.key}`}>
                    {f.options.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : f.type === "textarea" ? (
                  <textarea rows={3} value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} className={inputCls} data-testid={`field-${f.key}`} />
                ) : f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 py-2">
                    <input type="checkbox" checked={!!form[f.key]} onChange={e => set(f.key, e.target.checked)} className="w-4 h-4" data-testid={`field-${f.key}`} />
                    <span className="text-sm">Yes</span>
                  </label>
                ) : f.type === "number" ? (
                  <input type="number" step="0.01" value={form[f.key] ?? ""} onChange={e => set(f.key, Number(e.target.value))} className={inputCls} placeholder={f.placeholder} data-testid={`field-${f.key}`} />
                ) : (
                  <input value={form[f.key] || ""} onChange={e => set(f.key, e.target.value)} className={inputCls} placeholder={f.placeholder} data-testid={`field-${f.key}`} />
                )}
              </Field>
            </div>
          ))}
          <div className="md:col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase">Cancel</button>
            <button data-testid="save-entry-btn" disabled={saving} type="submit" className="px-4 py-2 bg-[#F97316] text-white text-xs font-bold uppercase">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// User-access widget for the Item Group form. "All Users" keeps the group
// open to everyone (default — matches all legacy groups); "Restricted"
// reveals a searchable user checklist. Admins always bypass restrictions.
function GroupAccessField({ form, set }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/users/directory").then(r => setUsers((r.data || []).filter(u => u.active !== false)));
  }, []);
  const mode = form.access_mode === "restricted" ? "restricted" : "all";
  const sel = form.allowed_users || [];
  const toggle = (id) => set("allowed_users", sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  const shown = users.filter(u => !q.trim() || `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));
  const btnCls = (active) => `flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border transition-colors ${active ? "bg-[#F97316] border-[#F97316] text-white" : "bg-white border-slate-300 text-slate-600 hover:border-[#F97316]"}`;
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button type="button" data-testid="group-access-all" className={btnCls(mode === "all")}
                onClick={() => { set("access_mode", "all"); }}>
          <Users size={12} className="inline mr-1 -mt-0.5" /> All Users
        </button>
        <button type="button" data-testid="group-access-restricted" className={btnCls(mode === "restricted")}
                onClick={() => set("access_mode", "restricted")}>
          <Lock size={12} className="inline mr-1 -mt-0.5" /> Restricted
        </button>
      </div>
      {mode === "all" ? (
        <p className="text-xs text-slate-500">Every user can see and transact with this group's items (default).</p>
      ) : (
        <div className="border border-slate-200">
          <div className="p-2 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users…"
                   className="flex-1 px-2 py-1.5 text-xs border border-slate-300 focus:outline-none focus:border-[#F97316]"
                   data-testid="group-access-user-search" />
            <span className="text-[10px] font-bold text-slate-500 uppercase whitespace-nowrap" data-testid="group-access-count">{sel.length} selected</span>
          </div>
          <div className="max-h-44 overflow-y-auto divide-y divide-slate-100">
            {shown.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">No users match.</div>}
            {shown.map(u => (
              <label key={u.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={sel.includes(u.id)} onChange={() => toggle(u.id)}
                       className="w-4 h-4" data-testid={`group-access-user-${u.id}`} />
                <span className="text-sm text-slate-800">{u.name || u.email}</span>
                {u.name && <span className="text-xs text-slate-400">{u.email}</span>}
              </label>
            ))}
          </div>
          <p className="px-3 py-2 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100">
            Only ticked users (plus admins) will see this group and its items in item pickers, the Items master and all transaction forms (PR / PO / PI / SI etc.).
          </p>
        </div>
      )}
    </div>
  );
}
